/**
 * Headless Browser & Bot Detection Utility
 *
 * Comprehensive detection system for headless browsers and automated traffic
 * CRITICAL: Designed to be SEO-friendly - legitimate search engine bots are NOT blocked
 *
 * Features:
 * - Multi-layered headless browser detection
 * - Search engine bot whitelist (Googlebot, Bingbot, etc.)
 * - Behavioral analysis integration
 * - Performance optimized with caching
 * - Detailed logging for monitoring
 */

import { logger } from "../utils/logger";

export interface HeadlessIndicators {
  isHeadless: boolean;
  isSearchEngineBot: boolean;
  confidence: number;
  indicators: string[];
  riskScore: number;
  botType?: string;
}

/**
 * Whitelist of legitimate search engine bots that should NEVER be blocked
 * These bots are essential for SEO and should always be allowed
 */
const SEARCH_ENGINE_BOTS = [
  // Google Bots
  { pattern: /googlebot/i, name: 'Googlebot', confidence: 1.0 },
  { pattern: /googlebot-mobile/i, name: 'Googlebot Mobile', confidence: 1.0 },
  { pattern: /googlebot-news/i, name: 'Googlebot News', confidence: 1.0 },
  { pattern: /googlebot-images/i, name: 'Googlebot Images', confidence: 1.0 },
  { pattern: /googlebot-video/i, name: 'Googlebot Video', confidence: 1.0 },
  { pattern: /mediapartners-google/i, name: 'Google Adsense Crawler', confidence: 1.0 },
  { pattern: /adsbot-google/i, name: 'Google Ads Bot', confidence: 1.0 },
  { pattern: /google-producer/i, name: 'Google Producer', confidence: 1.0 },

  // Bing/Microsoft Bots
  { pattern: /bingbot/i, name: 'Bingbot', confidence: 1.0 },
  { pattern: /msnbot/i, name: 'MSN Bot', confidence: 1.0 },
  { pattern: /bingpreview/i, name: 'Bing Preview', confidence: 1.0 },

  // Yahoo Bots
  { pattern: /slurp/i, name: 'Yahoo Slurp', confidence: 1.0 },

  // DuckDuckGo
  { pattern: /duckduckbot/i, name: 'DuckDuckBot', confidence: 1.0 },

  // Baidu
  { pattern: /baiduspider/i, name: 'Baidu Spider', confidence: 1.0 },

  // Yandex
  { pattern: /yandexbot/i, name: 'Yandex Bot', confidence: 1.0 },

  // Other legitimate crawlers
  { pattern: /facebookexternalhit/i, name: 'Facebook Crawler', confidence: 1.0 },
  { pattern: /facebookbot/i, name: 'Facebook Bot', confidence: 1.0 },
  { pattern: /meta-externalagent/i, name: 'Meta External Agent', confidence: 1.0 },
  { pattern: /meta-externalfetcher/i, name: 'Meta External Fetcher', confidence: 1.0 },
  { pattern: /twitterbot/i, name: 'Twitter Bot', confidence: 1.0 },
  { pattern: /linkedinbot/i, name: 'LinkedIn Bot', confidence: 1.0 },
  { pattern: /whatsapp/i, name: 'WhatsApp Crawler', confidence: 1.0 },
];

/**
 * Check if user agent belongs to a legitimate search engine bot
 * These should NEVER be blocked as they are essential for SEO
 */
function isSearchEngineBot(userAgent: string): { isBot: boolean; botName: string; confidence: number } {
  for (const { pattern, name, confidence } of SEARCH_ENGINE_BOTS) {
    if (pattern.test(userAgent)) {
      return { isBot: true, botName: name, confidence };
    }
  }
  return { isBot: false, botName: '', confidence: 0 };
}

/**
 * Comprehensive headless browser detection
 * Checks multiple indicators that headless browsers typically fail
 * SEO-SAFE: Search engine bots are explicitly allowed
 */
export function detectHeadlessBrowsers(): HeadlessIndicators {
  const indicators: string[] = [];
  let riskScore = 0;
  const maxRisk = 100;

  // CRITICAL: First check if this is a legitimate search engine bot
  const searchEngineCheck = isSearchEngineBot(navigator.userAgent);
  if (searchEngineCheck.isBot) {
    logger.info('[HeadlessDetection] ✅ Legitimate search engine bot detected', {
      botName: searchEngineCheck.botName,
      userAgent: navigator.userAgent.substring(0, 100)
    });

    return {
      isHeadless: false,
      isSearchEngineBot: true,
      confidence: 0, // No risk for legitimate bots
      indicators: [`Legitimate search engine bot: ${searchEngineCheck.botName}`],
      riskScore: 0,
      botType: searchEngineCheck.botName
    };
  }

  // 1. WebDriver property (most reliable indicator for automation)
  if (navigator.webdriver) {
    indicators.push('navigator.webdriver detected');
    riskScore += 25;
  }

  // 2. Chrome automation indicators
  if (typeof (window as any).chrome !== 'undefined') {
    const chrome = (window as any).chrome;
    if (chrome.runtime && chrome.runtime.onConnect === undefined) {
      indicators.push('Chrome automation detected');
      riskScore += 20;
    }
  }

  // 3. Plugins (headless browsers often have empty plugins)
  if (navigator.plugins && navigator.plugins.length === 0) {
    indicators.push('No plugins detected');
    riskScore += 15;
  }

  // 4. Languages (headless browsers may have limited languages)
  if (navigator.languages && navigator.languages.length === 0) {
    indicators.push('No languages detected');
    riskScore += 10;
  }

  // 5. Screen properties (headless browsers may have unusual dimensions)
  if (window.screen.width === 0 || window.screen.height === 0 ||
      window.screen.availWidth === 0 || window.screen.availHeight === 0) {
    indicators.push('Invalid screen dimensions');
    riskScore += 10;
  }

  // 6. WebGL renderer (headless browsers often have generic or missing WebGL)
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') as WebGLRenderingContext | null ||
               canvas.getContext('experimental-webgl') as WebGLRenderingContext | null;
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        const renderer = gl.getParameter((debugInfo as any).UNMASKED_RENDERER_WEBGL);
        if (renderer && (
          renderer.includes('Software') ||
          renderer.includes('Mesa') ||
          renderer.includes('SwiftShader')
        )) {
          indicators.push('Generic WebGL renderer detected');
          riskScore += 15;
        }
      }
    }
  } catch (e) {
    indicators.push('WebGL access failed');
    riskScore += 10;
  }

  // 7. Permissions API (headless browsers may not support this properly)
  if (typeof navigator.permissions === 'undefined') {
    indicators.push('Permissions API not available');
    riskScore += 5;
  }

  // 8. Timezone consistency check
  try {
    const timezoneOffset = new Date().getTimezoneOffset();
    const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!timezoneName || timezoneName === 'Etc/Unknown') {
      indicators.push('Invalid timezone detected');
      riskScore += 10;
    }
  } catch (e) {
    indicators.push('Timezone detection failed');
    riskScore += 5;
  }

  // 9. Memory info (headless browsers may report unusual memory)
  if ('memory' in performance) {
    const memInfo = (performance as any).memory;
    if (memInfo.jsHeapSizeLimit === 0 || memInfo.totalJSHeapSize === 0) {
      indicators.push('Zero memory limit detected');
      riskScore += 10;
    }
  }

  // 10. User interaction simulation detection
  let interactionScore = 0;
  const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
  events.forEach(event => {
    if (!(event in document)) {
      indicators.push(`Missing ${event} support`);
      interactionScore += 5;
    }
  });
  riskScore += interactionScore;

  // 11. Automation frameworks detection (high confidence indicators)
  const automationFrameworks = [
    'selenium', 'webdriver', 'phantom', 'puppeteer', 'playwright',
    'cypress', 'testcafe', 'nightmare', 'casper'
  ];

  const userAgent = navigator.userAgent.toLowerCase();
  automationFrameworks.forEach(framework => {
    if (userAgent.includes(framework)) {
      indicators.push(`Automation framework detected: ${framework}`);
      riskScore += 20;
    }
  });

  // 12. Check for unusual browser fingerprint patterns
  const fingerprintSuspicious =
    navigator.hardwareConcurrency === 0 ||
    (navigator as any).deviceMemory === 0;

  if (fingerprintSuspicious) {
    indicators.push('Suspicious browser fingerprint');
    riskScore += 15;
  }

  // 13. Connection type detection (headless browsers often show unusual connection types)
  if ('connection' in navigator) {
    const connection = (navigator as any).connection;
    if (connection && connection.effectiveType === '0') {
      indicators.push('Invalid connection type detected');
      riskScore += 5;
    }
  }

  // 14. Battery API (headless browsers may not have battery info)
  if ('getBattery' in navigator) {
    // This is async, but we can check if the method exists
    // Headless browsers sometimes don't implement this properly
  }

  // Determine final classification
  const isHeadless = riskScore >= 50;
  const confidence = Math.min(riskScore / maxRisk, 1);

  // Log detection results for monitoring
  if (isHeadless || riskScore > 20) {
    logger.warn('[HeadlessDetection] 🚨 Suspicious activity detected', {
      isHeadless,
      confidence,
      riskScore,
      indicators: indicators.slice(0, 5), // Limit log size
      userAgent: navigator.userAgent.substring(0, 100)
    });
  }

  return {
    isHeadless,
    isSearchEngineBot: false,
    confidence,
    indicators,
    riskScore
  };
}

/**
 * Enhanced user agent analysis for bot detection
 * SEO-SAFE: Returns search engine bot info for whitelisted crawlers
 * PERFORMANCE: Uses caching to avoid repeated expensive regex operations
 */
export function analyzeUserAgentForBots(userAgent: string): {
  isBot: boolean;
  isSearchEngineBot: boolean;
  botType: string;
  confidence: number;
  shouldBlock: boolean;
} {
  // Check cache first
  const now = Date.now();
  const cached = userAgentCache[userAgent];
  if (cached && (now - cached.timestamp) < USER_AGENT_CACHE_DURATION) {
    return cached.result;
  }

  const ua = userAgent.toLowerCase();

  // First, check for legitimate search engine bots (NEVER block these)
  const searchEngineCheck = isSearchEngineBot(userAgent);
  if (searchEngineCheck.isBot) {
    return {
      isBot: true,
      isSearchEngineBot: true,
      botType: searchEngineCheck.botName,
      confidence: 0, // No risk for legitimate bots
      shouldBlock: false // CRITICAL: Never block search engine bots
    };
  }

  // Known malicious bot patterns (these CAN be blocked)
  const maliciousBotPatterns = [
    { pattern: /selenium|webdriver/i, type: 'Selenium/WebDriver', confidence: 0.95, shouldBlock: true },
    { pattern: /puppeteer|playwright/i, type: 'Headless Browser', confidence: 0.9, shouldBlock: true },
    { pattern: /phantom|casper|nightmare/i, type: 'Headless Browser', confidence: 0.85, shouldBlock: true },
    { pattern: /chrome.*headless/i, type: 'Chrome Headless', confidence: 1.0, shouldBlock: true },
    { pattern: /curl|wget/i, type: 'HTTP Client', confidence: 0.8, shouldBlock: true },
    { pattern: /python-requests|java|node-fetch/i, type: 'Automated HTTP Client', confidence: 0.7, shouldBlock: true },
    { pattern: /scrapy|beautifulsoup|mechanize/i, type: 'Web Scraper', confidence: 0.8, shouldBlock: true },
  ];

  for (const { pattern, type, confidence, shouldBlock } of maliciousBotPatterns) {
    if (pattern.test(ua)) {
      return {
        isBot: true,
        isSearchEngineBot: false,
        botType: type,
        confidence,
        shouldBlock
      };
    }
  }

  // Generic bot detection (lower confidence, may be false positives)
  const genericBotPatterns = [
    /bot|crawler|spider|scraper/i
  ];

  for (const pattern of genericBotPatterns) {
    if (pattern.test(ua)) {
      // Additional check: exclude common browser extensions and legitimate tools
      const legitimateIndicators = [
        'chrome-extension', 'firefox', 'safari', 'edge',
        'postman', 'insomnia', 'httpie'
      ];

      const hasLegitimateIndicator = legitimateIndicators.some(indicator =>
        ua.includes(indicator)
      );

      if (!hasLegitimateIndicator) {
        return {
          isBot: true,
          isSearchEngineBot: false,
          botType: 'Generic Bot',
          confidence: 0.6,
          shouldBlock: true
        };
      }
    }
  }

  const result = {
    isBot: false,
    isSearchEngineBot: false,
    botType: '',
    confidence: 0,
    shouldBlock: false
  };

  // Cache the result
  userAgentCache[userAgent] = {
    result,
    timestamp: now
  };

  // Limit cache size to prevent memory leaks
  const cacheKeys = Object.keys(userAgentCache);
  if (cacheKeys.length > 100) {
    // Remove oldest entries (simple LRU approximation)
    const sortedKeys = cacheKeys.sort((a, b) =>
      userAgentCache[a].timestamp - userAgentCache[b].timestamp
    );
    const keysToRemove = sortedKeys.slice(0, 20); // Remove 20 oldest
    keysToRemove.forEach(key => delete userAgentCache[key]);
  }

  return result;
}

/**
 * Combined detection function that uses both client-side and user-agent analysis
 * This is the main entry point for bot detection
 */
export function detectBotsAndHeadlessBrowsers(): {
  isBot: boolean;
  isHeadless: boolean;
  isSearchEngineBot: boolean;
  shouldBlockAds: boolean;
  shouldBlockCompletely: boolean;
  confidence: number;
  details: {
    clientSide: HeadlessIndicators;
    userAgent: ReturnType<typeof analyzeUserAgentForBots>;
  };
} {
  const clientSideDetection = detectHeadlessBrowsers();
  const userAgentAnalysis = analyzeUserAgentForBots(navigator.userAgent);

  // Combine results
  const isBot = clientSideDetection.isHeadless || userAgentAnalysis.isBot;
  const isHeadless = clientSideDetection.isHeadless;
  const isSearchEngineBot = clientSideDetection.isSearchEngineBot || userAgentAnalysis.isSearchEngineBot;
  const maxConfidence = Math.max(clientSideDetection.confidence, userAgentAnalysis.confidence);

  // Decision logic for blocking
  let shouldBlockAds = false;
  let shouldBlockCompletely = false;

  if (isSearchEngineBot) {
    // NEVER block search engine bots - they're essential for SEO
    shouldBlockAds = false;
    shouldBlockCompletely = false;
  } else if (isBot || isHeadless) {
    // Block ads for detected bots/headless browsers
    shouldBlockAds = maxConfidence >= 0.7;
    // Only completely block for very high confidence detections
    shouldBlockCompletely = maxConfidence >= 0.9;
  }

  return {
    isBot,
    isHeadless,
    isSearchEngineBot,
    shouldBlockAds,
    shouldBlockCompletely,
    confidence: maxConfidence,
    details: {
      clientSide: clientSideDetection,
      userAgent: userAgentAnalysis
    }
  };
}

/**
 * Cache detection results to avoid repeated expensive checks
 */
let detectionCache: {
  result: ReturnType<typeof detectBotsAndHeadlessBrowsers> | null;
  timestamp: number;
  userAgent: string;
} = {
  result: null,
  timestamp: 0,
  userAgent: ''
};

const CACHE_DURATION = 30000; // 30 seconds

/**
 * User agent analysis cache to avoid repeated regex operations
 */
let userAgentCache: {
  [userAgent: string]: {
    result: ReturnType<typeof analyzeUserAgentForBots>;
    timestamp: number;
  };
} = {};

const USER_AGENT_CACHE_DURATION = 300000; // 5 minutes (user agents are static)

export function getCachedDetection(): ReturnType<typeof detectBotsAndHeadlessBrowsers> {
  const now = Date.now();
  const currentUserAgent = navigator.userAgent;

  // Check if we have a valid cached result for the same user agent
  if (detectionCache.result &&
      detectionCache.userAgent === currentUserAgent &&
      (now - detectionCache.timestamp) < CACHE_DURATION) {
    return detectionCache.result;
  }

  const result = detectBotsAndHeadlessBrowsers();
  detectionCache = {
    result,
    timestamp: now,
    userAgent: currentUserAgent
  };

  return result;
}
